import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  commaFormat,
  encodeSearchWord,
  getGender,
  getMonthStartEndText,
  timestamp2Localestring,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { SwRankDropbox } from '../commonComponent/SwRankDropbox';

const columns = ['순위', '학교', '금', '은', '동'];

const readColumns = [
  '순위',
  '지원청명',
  '학교',
  '사용자 수',
  '완독 권수',
  '금',
  '은',
  '동',
];

const stepColumns = [
  '순위',
  '지원청명',
  '학교',
  '사용자 수',
  '누적 걸음수',
  '평균 걸음수',
  '금',
  '은',
  '동',
];

const writeColumns = [
  '순위',
  '지원청명',
  '학교',
  '사용자 수',
  '작성글 수',
  '금',
  '은',
  '동',
];

interface Props {
  title: string;
  targetMonth: any;
  data: any;
  onDownload: () => void;
}

export const EducationOfficeSchoolRankTable: React.FC<Props> = ({
  title,
  targetMonth,
  data,
  onDownload,
}) => {
  const [selectItem, setSelectItem] = useState<number>(1);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [selectedSeq, setSelectedSeq] = useState<number>(-1);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState<string>('book-group');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSchoolClass, setOpenSchoolClass] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);
  const [openUserRank, setOpenUserRank] = useState<boolean>(false);

  const detailInfo = (idx: number) => {
    setSelectedSeq(data.rankList[idx].memberSeq);
    setOpenUserRank(true);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    const newData =
      title === KEY_READ_TEXT && selectItem === 1
        ? data?.rankList
            .slice()
            .sort((a: any, b: any) => b.finishCount - a.finishCount)
        : title === KEY_READ_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            return b.finishCount - a.finishCount;
          })
        : title === KEY_WALK_TEXT && selectItem === 1
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            return b.bronzeCount - a.silverCount;
          })
        : title === KEY_WALK_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.avgStep !== a.avgStep) {
              return b.avgStep - a.avgStep;
            }
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            return b.bronzeCount - a.silverCount;
          })
        : title === KEY_WALK_TEXT && selectItem === 3
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            return b.bronzeCount - a.silverCount;
          })
        : title === KEY_WRITE_TEXT && selectItem === 1
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.writeCount !== a.writeCount) {
              return b.writeCount - a.writeCount;
            }
            return b.goldCount - a.goldCount;
          })
        : title === KEY_WRITE_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            return b.writeCount - a.writeCount;
          })
        : [];
    setPage(1);
    setSortedData(newData);
  }, [data, title, selectItem]);

  useEffect(() => {
    data && setTotalRecords(data.totalRecordCount);
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <Typography variant='h4'>{title}</Typography>
        <Box className='flex_between'>
          <Box>
            {/* <Typography className='title'>
              달성기준(월) 금 <span style={{ color: '#2581FF' }}>3</span>권 은{' '}
              <span style={{ color: '#2581FF' }}>2</span>권 동{' '}
              <span style={{ color: '#2581FF' }}>1</span>권
            </Typography> */}
          </Box>
          <Box className='flex_start'>
            <Typography>
              기준 : {getMonthStartEndText(targetMonth * 1000)}
            </Typography>

            <Button
              sx={{ ml: '1rem' }}
              color='info'
              variant='outlined'
              onClick={onDownload}
            >
              데이터 다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Box>
          <SwRankDropbox title={title} setSelectItem={setSelectItem} />
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer sx={{ height: '55vh', overflowY: 'scroll' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {(title === KEY_READ_TEXT
                    ? readColumns
                    : title === KEY_WALK_TEXT
                    ? stepColumns
                    : writeColumns
                  ).map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData &&
                  sortedData.length > 0 &&
                  sortedData
                    ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
                    .map((row: any, idx: number) =>
                      title === KEY_READ_TEXT
                        ? [
                            row.rankOrder,
                            row.officeName,
                            row.schoolName,
                            commaFormat(row.memberCount),
                            commaFormat(row.finishCount),
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                        : title === KEY_WALK_TEXT
                        ? [
                            row.rankOrder,
                            row.officeName,
                            row.schoolName,
                            commaFormat(row.memberCount),
                            commaFormat(row.totalStep),
                            commaFormat(Math.round(row.avgStep)),
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                        : [
                            row.rankOrder,
                            row.officeName,
                            row.schoolName,
                            commaFormat(row.memberCount),
                            commaFormat(row.writeCount),
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                    )
                    .map((row: any, idx: number) => (
                      <TableRow key={idx.toString()}>
                        {(title === KEY_READ_TEXT
                          ? readColumns
                          : title === KEY_WALK_TEXT
                          ? stepColumns
                          : writeColumns
                        ).map((column: any, i: number) => (
                          <TableCell
                            key={`tbl_${title}_${i}`}
                            sx={{ p: '.7rem' }}
                          >
                            {row[i]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={
              data &&
              data.rankList &&
              Math.ceil(data.rankList.length / rowsPerPage)
            }
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      {loading && <SwLoading />}
    </>
  );
};
